import React from 'react'
import { Modal, Input, Label, Button, Checkbox, MessageEditor } from '@/shared/ui'
import { upsertFaq, type Faq } from '../services/faqService'

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  initial?: Partial<Faq>
}

export default function FaqFormModal({ open, onClose, onSaved, initial }: Props) {
  const [title, setTitle] = React.useState(initial?.title ?? '')
  const [content, setContent] = React.useState(initial?.content ?? '')
  const [visible, setVisible] = React.useState<boolean>(initial?.visible ?? true)
  const [position, setPosition] = React.useState<string>(
    typeof initial?.position === 'number' ? String(initial?.position) : '',
  )
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setTitle(initial?.title ?? '')
    setContent(initial?.content ?? '')
    setVisible(initial?.visible ?? true)
    setPosition(typeof initial?.position === 'number' ? String(initial?.position) : '')
    setSaving(false)
    setError(null)
  }, [open, initial])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      await upsertFaq({
        id: initial?.id,
        title: title.trim(),
        content: content.trim(),
        visible,
        position: position.trim() ? Number(position) : null,
      })
      onSaved()
      onClose()
    } catch (err: any) {
      setError(err?.message ?? 'Falha ao salvar FAQ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v) => { if (!v) onClose() }} className="p-6 sm:p-8">
      <div className="mb-4 text-xl font-semibold">
        {initial?.id ? 'Editar FAQ' : 'Nova FAQ'}
      </div>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
        {error && (
          <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Título</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título da dúvida"
            required
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Texto</Label>
          <MessageEditor value={content} onChange={setContent} height={300} />
          <div className="text-xs text-foreground/70">
            Dica: use a barra de ferramentas acima para formatar o texto e adicionar links externos.
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Posição (opcional)</Label>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="Ex.: 1"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Visível</Label>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={visible}
              onChange={(e) => setVisible((e.target as HTMLInputElement).checked)}
            />
            <span className="text-sm">Mostrar na home</span>
          </div>
        </div>
        <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

